import * as ExcelJS from 'exceljs';

export interface ParametriPCTO {
    oreDisponibili: number;
    coefficienteSettore: number;
    coefficienteArticolazione: number;
    coefficientePresenza: number;
    coefficienteDistanza: number;
    modalitaMonitoraggi: 'eventi' | 'abbinamento';
    tutorSettore?: { [idSettore: string]: number };
}

interface RigaDocente {
    id: number;
    nome: string;
    settori: Set<string>;
    abbinamenti: Set<string>;
    settore: number;
    articolazione: number;
    formativo: number;
    preliminari: number;
    presenza: number;
    distanza: number;
}

export function validaParametriPCTO(p: ParametriPCTO) {
    const valori = [p.oreDisponibili, p.coefficienteSettore, p.coefficienteArticolazione,
        p.coefficientePresenza, p.coefficienteDistanza];
    if (valori.some(v => typeof v !== 'number' || !Number.isFinite(v) || v < 0) || p.oreDisponibili <= 0) {
        throw new Error('Inserire ore disponibili maggiori di zero e coefficienti non negativi.');
    }
    if (p.modalitaMonitoraggi !== 'eventi' && p.modalitaMonitoraggi !== 'abbinamento') {
        throw new Error('Selezionare una modalità di conteggio dei monitoraggi.');
    }
}

const nomeNormalizzato = (nome: string) => String(nome || '').trim().replace(/\s+/g, ' ').toUpperCase();

export function preparaRendicontoPCTO(recordsets: any, p: ParametriPCTO, anno: number) {
    validaParametriPCTO(p);
    if (!Array.isArray(recordsets) || recordsets.length !== 5 || recordsets.some(s => !Array.isArray(s)) ||
        recordsets[0].length !== 1 || recordsets[0][0].Versione !== 1) {
        throw new Error('Dati PCTO non compatibili: aggiornare la procedura sp_ReportRendicontoPCTO.');
    }
    const [testata, abbinamenti, tutorSettore, monitoraggi, ispezioni] = recordsets;
    const intestazione = testata[0];
    if (intestazione.IdAnnoScolastico !== anno || intestazione.AbbinamentiTotali !== abbinamenti.length) {
        throw new Error('Anno o totale abbinamenti non coerenti nella risposta del report.');
    }
    if (!abbinamenti.length) throw new Error('Nessun abbinamento PCTO disponibile per l’anno selezionato.');
    const avvisi: string[] = [
        'I tutor di settore derivano dalle associazioni attuali: verificare gli incarichi per gli anni precedenti.',
        'Sono inclusi gli abbinamenti in attesa, accettati, confermati e terminati; esclusi provvisori, rifiutati e annullati.',
        'Le visite preliminari sono ispezioni accettate/terminate/confermate nell’anno scolastico presso aziende con abbinamenti PCTO; verificare che siano pertinenti al PCTO.'
    ];
    const docenti = new Map<number, RigaDocente>();
    const docente = (id: number, nome: string): RigaDocente | undefined => {
        if (!Number.isInteger(id) || id <= 0 || !String(nome || '').trim()) return undefined;
        if (!docenti.has(id)) docenti.set(id, { id, nome: nome.trim(), settori: new Set(), abbinamenti: new Set(),
            settore: 0, articolazione: 0, formativo: 0, preliminari: 0, presenza: 0, distanza: 0 });
        return docenti.get(id);
    };
    const chiave = (b: any) => `${b.IdAbbinamentoTutor}:${b.IdRichiesta}`;
    if (new Set(abbinamenti.map(chiave)).size !== abbinamenti.length) throw new Error('Abbinamenti duplicati nella risposta PCTO.');
    const assegna = (d: RigaDocente | undefined, b: any, ruolo: 'settore' | 'articolazione' | 'formativo') => {
        if (!d) { avvisi.push(`Abbinamento ${chiave(b)}: tutor ${ruolo} mancante.`); return; }
        d[ruolo]++;
        d.settori.add(b.Settore || 'Settore non disponibile');
        d.abbinamenti.add(chiave(b));
    };
    abbinamenti.forEach((b: any) => {
        let responsabili = tutorSettore.filter((t: any) => t.IdSettore === b.IdSettore);
        const scelta = p.tutorSettore && p.tutorSettore[b.IdSettore];
        if (scelta) {
            const candidato = tutorSettore.find((t: any) => t.IdDocente === scelta);
            const abbinamento = abbinamenti.find((a: any) => a.IdTutorArticolazione === scelta || a.IdTutorFormativo === scelta);
            const nome = candidato ? candidato.Docente : abbinamento ?
                (abbinamento.IdTutorArticolazione === scelta ? abbinamento.TutorArticolazione : abbinamento.TutorFormativo) : '';
            responsabili = nome ? [{ IdDocente: scelta, Docente: nome }] : [];
        }
        if (responsabili.length !== 1) {
            throw new Error(`Selezionare il tutor di settore per ${b.Settore || b.IdSettore}.`);
        } else assegna(docente(responsabili[0].IdDocente, responsabili[0].Docente), b, 'settore');
        assegna(docente(b.IdTutorArticolazione, b.TutorArticolazione), b, 'articolazione');
        assegna(docente(b.IdTutorFormativo, b.TutorFormativo), b, 'formativo');
    });
    const gruppi = new Map<string, { docente: RigaDocente; presenza: number; distanza: number }>();
    const idsReport = new Set<number>();
    const dettaglioMonitoraggi = monitoraggi.map((m: any) => {
        let esito = '';
        const presenza = ['In azienda', 'In cantiere'].indexOf(m.TipoVisita) >= 0;
        const distanza = ['Telefonicamente', 'Via Email', 'In Videochiamata'].indexOf(m.TipoVisita) >= 0;
        const candidati = abbinamenti.filter((b: any) => b.IdRichiesta === m.IdRichiesta &&
            nomeNormalizzato(b.TutorFormativo) === nomeNormalizzato(m.TutorFormativo));
        if (idsReport.has(m.IdReport)) throw new Error('Monitoraggi duplicati nella risposta PCTO.');
        idsReport.add(m.IdReport);
        if ([2, 4, 5].indexOf(m.IdStatoAccettazione) < 0) esito = 'Escluso: monitoraggio non convalidato';
        else if (!presenza && !distanza) esito = 'Escluso: tipo monitoraggio sconosciuto';
        else if (candidati.length !== 1) esito = 'Escluso: attribuzione al tutor/abbinamento non univoca';
        else {
            const b = candidati[0];
            const d = docenti.get(b.IdTutorFormativo);
            if (!d) esito = 'Escluso: tutor mancante';
            else {
                const key = chiave(b);
                if (!gruppi.has(key)) gruppi.set(key, { docente: d, presenza: 0, distanza: 0 });
                const gruppo = gruppi.get(key)!;
                if (presenza) gruppo.presenza++; else gruppo.distanza++;
                esito = p.modalitaMonitoraggi === 'eventi' ? 'Conteggiato' : 'Incluso nel riepilogo per abbinamento';
            }
        }
        if (esito.indexOf('Escluso') === 0) avvisi.push(`Monitoraggio ${m.IdReport}: ${esito}.`);
        return { ...m, Esito: esito };
    });
    gruppi.forEach(g => {
        g.docente.presenza += p.modalitaMonitoraggi === 'eventi' ? g.presenza : Number(g.presenza > 0);
        g.docente.distanza += p.modalitaMonitoraggi === 'eventi' ? g.distanza : Number(g.presenza === 0 && g.distanza > 0);
    });
    const idsIspezioni = new Set<number>();
    const dettaglioIspezioni = ispezioni.map((i: any) => {
        if (idsIspezioni.has(i.IdIspezione)) throw new Error('Ispezioni duplicate nella risposta PCTO.');
        idsIspezioni.add(i.IdIspezione);
        const valida = [2, 4, 5].indexOf(i.IdStatoAccettazione) >= 0;
        const d = valida ? docente(i.IdDocente, i.Docente) : undefined;
        if (d) d.preliminari++;
        const esito = d ? 'Conteggiata: 1 ora' : 'Esclusa: non convalidata o docente mancante';
        if (!d) avvisi.push(`Ispezione ${i.IdIspezione}: ${esito}.`);
        return { ...i, Esito: esito };
    });
    const righe = Array.from(docenti.values()).sort((a, b) =>
        Array.from(a.settori).join(', ').localeCompare(Array.from(b.settori).join(', '), 'it') || a.nome.localeCompare(b.nome, 'it'));
    return { intestazione, righe, avvisi: Array.from(new Set(avvisi)), abbinamenti,
        monitoraggi: dettaglioMonitoraggi, ispezioni: dettaglioIspezioni };
}

export function impostaRipartizionePCTO(sheet: ExcelJS.Worksheet, p: ParametriPCTO) {
    // Il modello originale contiene righe nascoste: i parametri devono essere
    // visibili anche quando si genera il report da una copia di quel modello.
    for (let riga = 1; riga <= 17; riga++) {
        sheet.getRow(riga).hidden = false;
    }
    sheet.getCell('A8').value = 'RIPARTIZIONE';
    const ripartizioni = [
        { riga: 9, ruolo: 'TUTOR DI SETTORE', valore: p.coefficienteSettore, descrizione: '' },
        { riga: 10, ruolo: 'TUTOR DI ARTICOLAZIONE', valore: p.coefficienteArticolazione, descrizione: '' },
        { riga: 12, ruolo: 'TUTOR FORMATIVO', valore: p.coefficientePresenza, descrizione: 'visita in presenza' },
        { riga: 13, ruolo: 'TUTOR FORMATIVO', valore: p.coefficienteDistanza, descrizione: 'solo telefonata/video call/email' }
    ];
    ripartizioni.forEach(({ riga, ruolo, valore, descrizione }) => {
        sheet.getCell(`A${riga}`).value = ruolo;
        const percentuale = sheet.getCell(`B${riga}`);
        percentuale.value = valore;
        percentuale.numFmt = '0.00%';
        percentuale.font = { ...percentuale.font, bold: true };
        sheet.getCell(`C${riga}`).value = descrizione;
        sheet.getCell(`C${riga}`).alignment = { horizontal: 'left', vertical: 'middle', wrapText: true };
        // Stessa posizione delle indicazioni manuali del documento originale.
        sheet.unMergeCells(`D${riga}:H${riga}`);
        sheet.mergeCells(`D${riga}:H${riga}`);
        const indicazione = sheet.getCell(`D${riga}`);
        indicazione.value = 'Valore impostato in ValtIS';
        indicazione.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFF00' } };
        indicazione.alignment = { horizontal: 'left', vertical: 'middle' };
        sheet.getRow(riga).height = descrizione ? 45 : 20;
    });
}

export function compilaRendicontoPCTO(workbook: ExcelJS.Workbook, recordsets: any, p: ParametriPCTO, anno: number) {
    const dati = preparaRendicontoPCTO(recordsets, p, anno);
    const sheet = workbook.getWorksheet(1);
    if (!sheet) throw new Error('Template PCTO privo del foglio principale.');
    // Conserva intestazione e stile, elimina integralmente dati e formule del facsimile.
    const stili = Array.from({ length: 16 }, (_, i) => JSON.parse(JSON.stringify(sheet.getRow(18).getCell(i + 1).style)));
    sheet.unMergeCells('L55:M55');
    // ExcelJS 4.4 non rimuove sempre le righe in coda con spliceRows:
    // azzera prima le celle per impedire residui e formule condivise del modello.
    for (let r = 18; r <= sheet.rowCount; r++) {
        const row = sheet.getRow(r);
        row.values = [];
        row.height = sheet.properties.defaultRowHeight;
    }
    sheet.spliceRows(18, sheet.rowCount - 17);
    const set = (address: string, value: any) => { sheet.getCell(address).value = value; };
    set('A1', `RENDICONTAZIONE PCTO ESTIVI ${dati.intestazione.AnnoScolastico}`);
    set('B4', dati.intestazione.AbbinamentiTotali);
    set('C4', 'ABBINAMENTI');
    set('B5', p.oreDisponibili);
    ['D4', 'D5'].forEach(a => set(a, null));
    impostaRipartizionePCTO(sheet, p);
    const unitario = p.oreDisponibili / dati.intestazione.AbbinamentiTotali;
    set('B6', { formula: 'IFERROR(B5/B4,0)', result: unitario });
    set('C16', 'ABBINAMENTI'); set('C17', 'DISTINTI PER DOCENTE');
    ['D17', 'F17', 'K17'].forEach(a => set(a, 'N° ABBINAMENTI'));
    set('J17', '1 ORA / VISITA');
    set('A15', 'TUTOR DI SETTORE E ARTICOLAZIONE');
    set('K15', p.modalitaMonitoraggi === 'eventi' ? 'MONITORAGGI CONVALIDATI: EVENTI' : 'MONITORAGGI: UNA QUOTA PER ABBINAMENTO');
    const sums: { [key: string]: number } = {};
    dati.righe.forEach((d, index) => {
        const r = 18 + index;
        const row = sheet.getRow(r);
        stili.forEach((style, i) => { row.getCell(i + 1).style = JSON.parse(JSON.stringify(style)); });
        const e = d.settore * unitario * p.coefficienteSettore;
        const g = d.articolazione * unitario * p.coefficienteArticolazione;
        const n = d.preliminari + d.presenza * unitario * p.coefficientePresenza + d.distanza * unitario * p.coefficienteDistanza;
        const i = Math.ceil(Number((e + g).toFixed(10)));
        const o = Math.ceil(Number(n.toFixed(10)));
        const values: { [key: string]: number } = { C: d.abbinamenti.size, D: d.settore, E: e, F: d.articolazione,
            G: g, H: e + g, I: i, J: d.preliminari, K: d.formativo, L: d.presenza, M: d.distanza, N: n, O: o, P: i + o };
        set(`A${r}`, d.nome); set(`B${r}`, Array.from(d.settori).join(', ') || 'Visite preliminari');
        const formulas: { [key: string]: string } = { E: `D${r}*$B$6*$B$9`, G: `F${r}*$B$6*$B$10`,
            H: `E${r}+G${r}`, I: `ROUNDUP(ROUND(H${r},10),0)`,
            N: `J${r}+L${r}*$B$6*$B$12+M${r}*$B$6*$B$13`, O: `ROUNDUP(ROUND(N${r},10),0)`, P: `I${r}+O${r}` };
        Object.keys(values).forEach(c => {
            set(`${c}${r}`, formulas[c] ? { formula: formulas[c], result: values[c] } : values[c]);
            sums[c] = (sums[c] || 0) + values[c];
        });
    });
    const last = 17 + dati.righe.length;
    const total = last + 2;
    set(`A${total}`, 'TOTALE');
    Object.keys(sums).forEach(c => set(`${c}${total}`, { formula: `SUM(${c}18:${c}${last})`, result: sums[c] }));
    sheet.getRow(total).font = { bold: true };
    set(`A${total + 2}`, 'TOTALE ORE');
    set(`C${total + 2}`, { formula: `I${total}+O${total}`, result: sums.P || 0 });
    set(`A${total + 4}`, 'ORE RESIDUE DA CONTRATTAZIONE');
    set(`C${total + 4}`, { formula: `$B$5-C${total + 2}`, result: p.oreDisponibili - (sums.P || 0) });
    if ((sums.P || 0) > p.oreDisponibili) dati.avvisi.push('Le ore da riconoscere superano la disponibilità da contrattazione.');
    set(`A${total + 6}`, 'Vedere il foglio Controlli per criteri di conteggio e dati da verificare.');
    sheet.getColumn('A').width = 36; sheet.getColumn('B').width = 25;
    for (let c = 3; c <= 16; c++) sheet.getColumn(c).width = 15;
    sheet.views = [{ state: 'normal' }];
    sheet.pageSetup = { ...sheet.pageSetup, orientation: 'landscape', fitToPage: true, fitToWidth: 1, fitToHeight: 0,
        printArea: `A1:P${total + 6}`, printTitlesRow: '16:17' };
    workbook.calcProperties.fullCalcOnLoad = true;
    const dettaglio = (name: string, rows: any[], columns: string[]) => {
        const s = workbook.addWorksheet(name);
        s.addRow(columns);
        rows.forEach(r => s.addRow(columns.map(c => r[c] === undefined ? null : r[c])));
        s.getRow(1).font = { bold: true };
        s.columns.forEach(c => { c.width = 24; });
        s.views = [{ state: 'normal' }];
        if (rows.length) s.autoFilter = { from: { row: 1, column: 1 }, to: { row: rows.length + 1, column: columns.length } };
    };
    dettaglio('Abbinamenti', dati.abbinamenti, ['IdRichiesta', 'IdAbbinamentoTutor', 'IdAllievo', 'Allievo', 'Settore',
        'IdTutorArticolazione', 'TutorArticolazione', 'IdTutorFormativo', 'TutorFormativo', 'Azienda', 'IdStatoAccettazione']);
    dettaglio('Monitoraggi', dati.monitoraggi, ['IdReport', 'IdRichiesta', 'TutorFormativo', 'TipoVisita', 'DataVisita', 'IdStatoAccettazione', 'Esito']);
    dettaglio('Visite preliminari', dati.ispezioni, ['IdIspezione', 'IdDocente', 'Docente', 'IdAzienda', 'DataIspezione', 'IdStatoAccettazione', 'Esito']);
    dettaglio('Controlli', [
        { Nota: `Anno ValtIS: ${anno}; anno scolastico: ${dati.intestazione.AnnoScolastico}.` },
        { Nota: `Abbinamenti: ${dati.intestazione.AbbinamentiTotali}; studenti distinti: ${dati.intestazione.StudentiDistinti}.` },
        { Nota: 'I tutor di settore sono verificati/selezionati dall’operatore prima dell’esportazione.' },
        { Nota: 'Le quote organizzativa e formativa sono arrotondate separatamente per docente. La colonna C non è sommabile come totale studenti dell’istituto.' },
        { Nota: 'Sono riconosciuti solo monitoraggi e ispezioni negli stati accettato, terminato o confermato (2, 4, 5).' },
        { Nota: p.modalitaMonitoraggi === 'eventi' ? 'Ogni monitoraggio convalidato vale una quota.' : 'Una quota per abbinamento: prevale la presenza; distanza soltanto in assenza di visite in presenza.' },
        ...dati.avvisi.map(Nota => ({ Nota }))
    ], ['Nota']);
    workbook.getWorksheet('Controlli')!.getColumn(1).width = 120;
    workbook.getWorksheet('Controlli')!.getColumn(1).alignment = { wrapText: true };
    return dati;
}
